// PRD §3.2 — "Linked Questionnaires" read-only block inside the Item detail.
// Questions are owned by the Questionnaire Design feature (PRD 05) and auto-
// sync onto items there; this view is informational + a deep link out for
// editing.
//
// Per FE plan, weights / confidences are surfaced read-only here in Slice 9.
// PRD §3.2 marks the editable per-row inputs as ⏸️ TBD (research review
// pending); when the research team confirms, that editing UI will follow.
//
// The "Edit in Questionnaire Design" link opens a new tab — Questionnaire
// Design lives at /questionnaire-design (see AdminNavigation.ts).

import { useTranslation } from 'react-i18next';
import { ExternalLink, Info, Link2Off, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { LinkedQuestion } from '../types/pro-item-design.types';

interface LinkedQuestionsSectionProps {
  linkedQuestions: readonly LinkedQuestion[];
  // SHCT-390 — the current item's pid (P-XXXX). In the shared PRO tree the QD
  // item node id IS the pid, so `?item=<pid>` lets QD open on this exact item.
  itemPid: string;
}

const QUESTIONNAIRE_DESIGN_PATH = '/questionnaire-design';

export function LinkedQuestionsSection({ linkedQuestions, itemPid }: LinkedQuestionsSectionProps) {
  const { t } = useTranslation('pro-item-design');
  const count = linkedQuestions.length;
  // SHCT-390 — deep-link target that opens Questionnaire Design on this item
  // (tree expanded to the exact item level) instead of QD's default view.
  const editInSearch = `?item=${encodeURIComponent(itemPid)}`;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <LinkIcon size={14} />
          {t('item.fields.linkedQuestions.sectionLabelWithCount', { count })}
        </span>
        <Link
          to={{ pathname: QUESTIONNAIRE_DESIGN_PATH, search: editInSearch }}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 11,
            color: 'var(--info)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <ExternalLink size={12} />
          {t('item.fields.linkedQuestions.editInLink')}
        </Link>
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted-foreground)',
          marginBottom: 8,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Info size={12} style={{ flexShrink: 0 }} />
        <span>{t('item.fields.linkedQuestions.autoSyncHint')}</span>
      </div>

      {count === 0 ? (
        <EmptyLinkedQuestions />
      ) : (
        <LinkedQuestionsTable rows={linkedQuestions} editInSearch={editInSearch} />
      )}
    </>
  );
}

function EmptyLinkedQuestions() {
  const { t } = useTranslation('pro-item-design');
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        color: 'var(--muted-foreground)',
        padding: 24,
        border: '1px dashed var(--border)',
        borderRadius: 6,
      }}
    >
      <Link2Off size={20} style={{ marginBottom: 8 }} />
      <p style={{ fontSize: 13, margin: 0 }}>{t('item.fields.linkedQuestions.empty')}</p>
    </div>
  );
}

function LinkedQuestionsTable({
  rows,
  editInSearch,
}: {
  rows: readonly LinkedQuestion[];
  editInSearch: string;
}) {
  const { t } = useTranslation('pro-item-design');
  return (
    <table className="table" style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th style={{ width: 80 }}>{t('item.fields.linkedQuestions.columns.qid')}</th>
          <th>{t('item.fields.linkedQuestions.columns.title')}</th>
          <th style={{ width: 80, textAlign: 'center' }}>
            {t('item.fields.linkedQuestions.columns.target')}
          </th>
          <th style={{ width: 70, textAlign: 'center' }}>
            {t('item.fields.linkedQuestions.columns.scoreRange')}
          </th>
          <th style={{ width: 80, textAlign: 'center' }}>
            {t('item.fields.linkedQuestions.columns.weightConfidence')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <LinkedQuestionRow key={row.qid} row={row} editInSearch={editInSearch} />
        ))}
      </tbody>
    </table>
  );
}

function LinkedQuestionRow({
  row,
  editInSearch,
}: {
  row: LinkedQuestion;
  editInSearch: string;
}) {
  const { t } = useTranslation('pro-item-design');
  const wc = (row.weight * row.confidence).toFixed(2);
  const targetClass =
    row.target === 'GUARDIAN' ? 'badge badge-warning' : 'badge badge-primary';
  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap' }}>
        <Link
          // SHCT-390 — every linked-question row belongs to this item, so the
          // qid link opens QD on the same item (tree expanded to it).
          to={{
            pathname: QUESTIONNAIRE_DESIGN_PATH,
            search: editInSearch,
          }}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--info)',
            fontWeight: 600,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {row.qid}
          <ExternalLink size={10} />
        </Link>
      </td>
      <td
        style={{
          maxWidth: 260,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={row.title}
      >
        {row.title}
      </td>
      <td style={{ textAlign: 'center' }}>
        <span className={targetClass} style={{ fontSize: 11 }}>
          {t(`item.fields.linkedQuestions.targetBadge.${row.target}`)}
        </span>
      </td>
      <td style={{ textAlign: 'center' }}>{row.scoreRange}</td>
      <td style={{ textAlign: 'center', fontWeight: 600 }}>{wc}</td>
    </tr>
  );
}
